// src/Sections/HeroSection/styles.ts
import styled from 'styled-components';
import { MEDIA_BREAKPOINTS, SHORT_HEIGHT } from '@/constants/layout';
import Container from '@/components/StyledComponents/Container';
import CircleImageWithText from '@/components/StyledComponents/CircleImageWithText';

export const VisuallyHidden = styled.div`
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

export const HeroSectionContainer = styled.section`
  --orbit-pad: 72px;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: visible;
  overflow-x: clip;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    min-height: 100dvh;
    padding-top: calc(env(safe-area-inset-top, 0px) + 15px);
    padding-bottom: calc(env(safe-area-inset-bottom, 10px) + var(--orbit-pad) * 0.5);

    --rail-gap: clamp(6px, 1.5vh, 18px);
    row-gap: var(--rail-gap);

    min-height: 0;
  }
`;

const RowBase = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: visible;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-top: 10px;
  }
`;

export const TopContainer = styled(RowBase)``;

export const CenterContainer = styled(RowBase)`
  flex: 1 1 auto;
  justify-content: center;
  align-self: stretch;
  min-height: 0;
`;

export const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  border: 2px solid rgba(216, 166, 64, 0.5);
  border-radius: 20px;
  background:
    radial-gradient(circle at 50% 50%, rgba(216, 166, 64, 0.18), rgba(0, 0, 0, 0) 38%),
    linear-gradient(135deg, rgba(10, 10, 12, 0.88), rgba(0, 0, 0, 0.72));
  box-shadow: inset 0 0 28px rgba(0, 0, 0, 0.45);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    margin: auto;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.9);
    background: rgba(0, 0, 0, 0.35);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    margin: auto;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 16px solid rgba(255, 255, 255, 0.95);
    transform: translateX(3px);
  }
`;

export const BottomContainer = styled(RowBase)`
  margin-top: clamp(10px, 4vh, 40px);
  padding-bottom: 10px;
  position: relative;
  overflow: visible;
  min-height: 220px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-top: clamp(2px, 1.2vh, 12px);
    padding-bottom: calc(1px + var(--orbit-pad) * 0.5);
    min-height: 180px; /* היה 200px */
  }

  @media (max-height: ${SHORT_HEIGHT}) and (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-top: clamp(0px, 0.6vh, 6px);
    min-height: 160px;
  }
`;

export const Circle = styled(CircleImageWithText)`
  --size: min(60vw, 320px);
  width: var(--size);
  height: var(--size);
  aspect-ratio: 1 / 1;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    --size: 180px;

    transform: translateY(-5px);
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: 560px) {
    transform: translateY(-6px);
  }
`;

export const LogoWrapper = styled.div`
  margin-bottom: clamp(3px, 0.7vh, 8px);

  @media (max-height: ${SHORT_HEIGHT}) and (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-bottom: 2px;
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

export const DescriptionWrapper = styled(Container)`
  overflow: visible;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (min-height: 800px) {
    margin-bottom: 10px;
  }
`;

export const DescriptionPad = styled.div`
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (min-height: 800px) {
    padding-top: 15px !important;
  }
`;

export const VideoStyle = styled.div`
  transition:
    width 0.3s ease,
    height 0.3s ease; // Add this line for smooth transitions

  width: ${({ theme }) => theme.sizes.small.videoWidth};
  height: ${({ theme }) => theme.sizes.small.videoHeight};

  @media (min-width: ${MEDIA_BREAKPOINTS.smallScreen}) {
    width: ${({ theme }) => theme.sizes.small.videoWidth};
    height: ${({ theme }) => theme.sizes.small.videoHeight};
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    width: ${({ theme }) => theme.sizes.medium.videoWidth};
    height: ${({ theme }) => theme.sizes.medium.videoHeight};
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
    width: ${({ theme }) => theme.sizes.large.videoWidth};
    height: ${({ theme }) => theme.sizes.large.videoHeight};
  }
`;


export const VideoBox = styled(VideoStyle)`
  width: 100%;
  height: auto;
  margin-inline: auto;

  /* מובייל */
  max-width: 720px;
  aspect-ratio: 16 / 9;
  max-height: clamp(180px, 46vh, 520px);

  display: flex;
  align-items: center;
  justify-content: center;

  > * { width: 100%; height: 100%; }

  video,
  iframe {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  /* דסקטופ - גדל קצת, לא ענק */
  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    max-width: min(940px, 72vw, calc(56svh * 16 / 9));
    max-height: 56svh;
  }

  /* דסקטופ גדול - תקרה שמרנית */
  @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
    max-width: min(1040px, 64vw, calc(60svh * 16 / 9));
    max-height: 60svh;
  }
`;

export const ShortViewportGuard = styled.div`
  overflow: visible;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: 560px) {
    ${HeroSectionContainer} {
      --rail-gap: clamp(6px, 2vh, 18px);
      padding-bottom: calc(env(safe-area-inset-bottom, 10px) + var(--orbit-pad) * 0.5);
    }

    ${VideoBox} {
      max-height: clamp(150px, 40vh, 420px);
    }

    ${Circle} {
      --size: clamp(110px, 16vh, 150px);
    }
  }
`;
